/**
 * Money handling. Everything internal is integer minor units.
 */
export const CURRENCIES = {
    AED: { code: 'AED', exponent: 2, symbol: 'AED', symbolAr: 'د.إ' },
    SAR: { code: 'SAR', exponent: 2, symbol: 'SAR', symbolAr: 'ر.س' },
    QAR: { code: 'QAR', exponent: 2, symbol: 'QAR', symbolAr: 'ر.ق' },
    KWD: { code: 'KWD', exponent: 3, symbol: 'KWD', symbolAr: 'د.ك' },
    BHD: { code: 'BHD', exponent: 3, symbol: 'BHD', symbolAr: 'د.ب' },
    OMR: { code: 'OMR', exponent: 3, symbol: 'OMR', symbolAr: 'ر.ع' },
    USD: { code: 'USD', exponent: 2, symbol: '$', symbolAr: '$' },
    EUR: { code: 'EUR', exponent: 2, symbol: '€', symbolAr: '€' },
    GBP: { code: 'GBP', exponent: 2, symbol: '£', symbolAr: '£' },
    INR: { code: 'INR', exponent: 2, symbol: '₹', symbolAr: '₹' },
    EGP: { code: 'EGP', exponent: 2, symbol: 'EGP', symbolAr: 'ج.م' },
    JPY: { code: 'JPY', exponent: 0, symbol: '¥', symbolAr: '¥' },
};
export function currencyInfo(code) {
    return CURRENCIES[code.toUpperCase()] ?? { code: code.toUpperCase(), exponent: 2, symbol: code.toUpperCase(), symbolAr: code.toUpperCase() };
}
export function isSupportedCurrency(code) {
    return Object.prototype.hasOwnProperty.call(CURRENCIES, code.toUpperCase());
}
/** Arabic-Indic and Eastern Arabic-Indic digits → ASCII. */
const DIGIT_MAP = {
    '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4',
    '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
    '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4',
    '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
};
export function normalizeDigits(input) {
    return input.replace(/[٠-٩۰-۹]/g, (d) => DIGIT_MAP[d] ?? d);
}
/**
 * Parse a human-written amount ("1,234.56", "١٢٣٤٫٥٦", "1.234,56") into minor units.
 * Returns null when the string is not a plausible amount.
 */
export function parseAmountToMinor(input, currency) {
    if (!input)
        return null;
    let s = normalizeDigits(input).trim();
    // Arabic decimal separator (U+066B) and thousands separator (U+066C).
    s = s.replace(/٫/g, '.').replace(/٬/g, ',');
    s = s.replace(/[\s  ]/g, '');
    if (!/[0-9]/.test(s))
        return null;
    s = s.replace(/[^0-9.,-]/g, '');
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma !== -1 && lastDot !== -1) {
        // Whichever separator appears last is the decimal separator.
        if (lastComma > lastDot)
            s = s.replace(/\./g, '').replace(',', '.');
        else
            s = s.replace(/,/g, '');
    }
    else if (lastComma !== -1) {
        const decimals = s.length - lastComma - 1;
        const multiple = s.indexOf(',') !== lastComma;
        // "1,500" and "1,234,567" are thousands-grouped; "1,50" is a decimal comma.
        if (decimals === 3 || multiple)
            s = s.replace(/,/g, '');
        else
            s = s.replace(',', '.');
    }
    s = s.replace(/,/g, '');
    const value = Number(s);
    if (!Number.isFinite(value))
        return null;
    const { exponent } = currencyInfo(currency);
    const minor = Math.round(value * 10 ** exponent);
    if (!Number.isSafeInteger(minor))
        return null;
    return minor;
}
export function minorToDecimal(minor, currency) {
    const { exponent } = currencyInfo(currency);
    return minor / 10 ** exponent;
}
export function decimalToMinor(value, currency) {
    const { exponent } = currencyInfo(currency);
    return Math.round(value * 10 ** exponent);
}
export function formatMoney(minor, currency, opts = {}) {
    const { locale = 'en', bare = false, compact = false } = opts;
    const info = currencyInfo(currency);
    const value = minorToDecimal(minor, currency);
    const nf = new Intl.NumberFormat(locale === 'ar' ? 'ar-AE' : 'en-AE', {
        minimumFractionDigits: compact ? 0 : info.exponent,
        maximumFractionDigits: compact ? 0 : info.exponent,
    });
    const num = nf.format(value);
    if (bare)
        return num;
    const symbol = locale === 'ar' ? info.symbolAr : info.symbol;
    return locale === 'ar' ? `${num} ${symbol}` : `${symbol} ${num}`;
}
/** Sum helper that keeps integer semantics explicit at call sites. */
export function sumMinor(values) {
    let total = 0;
    for (const v of values)
        total += v;
    return total;
}
/** Percentage change from `previous` to `current`, or null when undefined. */
export function pctChange(previous, current) {
    if (previous === 0)
        return null;
    return ((current - previous) / Math.abs(previous)) * 100;
}
//# sourceMappingURL=money.js.map